import Rails from '@rails/ujs';
import { Controller } from 'stimulus';

const TOP_THRESHOLD_PX = 24;

const TASK_DROPDOWN_DISABLED_CLASSES = ['opacity-50', 'pointer-events-none', 'cursor-not-allowed'];

export default class extends Controller {
  static targets = [
    'form',
    'link',
    'section',
    'scrollContainer',
    'taskContainer',
    'taskMenuItem',
    'responsesSummaryView',
    'learnerResponsesView'
  ];

  connect() {
    if (!this.hasSectionTarget || !this.hasScrollContainerTarget) return;

    this.onScroll = () => this.updateActiveLink();
    this.scrollContainerTarget.addEventListener('scroll', this.onScroll);
    this.updateActiveLink();
  }

  disconnect() {
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.removeEventListener('scroll', this.onScroll);
    }
  }

  submit() {
    this.formTarget.requestSubmit();
  }

  // Filters the task dropdown to the chosen program client-side, without
  // reloading the page - the task list is already fully loaded, so there's
  // nothing the server needs to compute just from a program pick.
  programChanged(event) {
    if (!this.hasTaskContainerTarget) return;

    const programId = event.target.value;

    this.taskMenuItemTargets.forEach((item) => {
      item.classList.toggle('hidden', item.dataset.programId !== programId);
    });

    TASK_DROPDOWN_DISABLED_CLASSES.forEach((className) => {
      this.taskContainerTarget.classList.toggle(className, !programId);
    });

    const taskDropdown = this.application.getControllerForElementAndIdentifier(
      this.taskContainerTarget.firstElementChild,
      'components--admin-dropdowns-dropdown'
    );
    const selectedTaskId = taskDropdown?.hiddenFieldTarget?.value;

    if (!selectedTaskId) return;

    const selectedTaskStillVisible = this.taskMenuItemTargets.some(
      (item) => item.dataset.value === selectedTaskId && item.dataset.programId === programId
    );

    if (!selectedTaskStillVisible) taskDropdown.resetSelection();
  }

  // Switches between the two overview sub-views without a full reload. Each
  // view is fetched once and cached in the DOM (data-loaded) - switching back
  // to an already-loaded view just toggles visibility, no request involved.
  viewChanged(event) {
    const url = event.detail.value;
    const view = new URL(url, window.location.origin).searchParams.get('view');
    const isLearnerResponses = view === 'learner_responses';
    const target = isLearnerResponses ?
      this.learnerResponsesViewTarget :
      this.responsesSummaryViewTarget;
    const other = isLearnerResponses ?
      this.responsesSummaryViewTarget :
      this.learnerResponsesViewTarget;

    other.classList.add('hidden');
    target.classList.remove('hidden');

    const historyUrl = new URL(window.location);
    historyUrl.searchParams.set('view', view);
    history.pushState({}, '', historyUrl);

    if (target.dataset.loaded === 'true') return;

    Rails.ajax({ url, type: 'GET', dataType: 'script' });
  }

  navigateToSection(event) {
    event.preventDefault();

    const pageId = event.currentTarget.dataset.pageId;
    const target = this.sectionTargets.find((section) => section.dataset.pageId === pageId);

    this.setActiveLink(pageId);
    target?.scrollIntoView({ block: 'start' });
  }

  // The "current" section is the last one (in document order) whose top has
  // scrolled up past the threshold - i.e. the section we're currently reading.
  // Defaults to the first section when nothing has scrolled past yet.
  updateActiveLink() {
    const threshold = this.scrollContainerTarget.getBoundingClientRect().top + TOP_THRESHOLD_PX;

    let current = this.sectionTargets[0];

    this.sectionTargets.forEach((section) => {
      if (section.getBoundingClientRect().top <= threshold) {
        current = section;
      }
    });

    this.setActiveLink(current?.dataset.pageId);
  }

  setActiveLink(pageId) {
    this.linkTargets.forEach((link) => {
      const isActive = link.dataset.pageId === pageId;

      link.classList.toggle('border-purple-700', isActive);
      link.classList.toggle('shadow-sm', isActive);
      link.classList.toggle('bg-white', isActive);
      link.classList.toggle('border-transparent', !isActive);
    });
  }
}
